import { formatAddressDisplay } from "@/features/applicant/utils/addressHelpers";
import { formatDateMMDDYY, formatPhoneNumber } from "../../../utils/applicationFormatters";
import { Home, MapPin, User } from "lucide-react";
import styles from "../../../styles/applicationDetail.module.css";
import actionBtnStyles from "../../../styles/actionButtons.module.css";
import { PreviousApplicationsModal } from "../../modals/PreviousApplicationsModal";
import { AboutYourselfConfirmDialogs } from "./AboutYourselfConfirmDialogs";
import { AddressView } from "./AddressView";
import type { AboutYourselfReadOnlyViewProps } from "../../../types/tabs";
import { VerifastIdentitySection } from "../VerifastIdentitySection";

const calculateAge = (birthdate: string | null | undefined): string => {
  if (!birthdate) return "";
  const birth = new Date(birthdate);
  if (Number.isNaN(birth.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birth.getDate())
  ) {
    age--;
  }
  return `${age} years`;
};

const formatSSN = (ssn: string | null | undefined): string => {
  if (!ssn) return "XXX-XX-XXXX";
  const last4 = ssn.slice(-4);
  return `XXX-XX-${last4}`;
};

const formatEmail = (email: string | null | undefined): string => {
  return email || "";
};

export function AboutYourselfReadOnlyView({
  view = "personal",
  applicationAllData,
  applicationData,
  applicantId,
  userData,
  currentAddress,
  previousAddresses,
  alternateAddresses,
  showPrimaryApplicantActions,
  showResidentButton,
  showDefaultButton,
  rentalStatusLine,
  previousAppsOpen,
  previousAppsCount,
  pendingVerifyEmail,
  pendingSendLink,
  canValidateEmail,
  canViewAddressOnMap,
  verifyingEmail,
  onSetPreviousAppsOpen: setPreviousAppsOpen,
  onOpenResident: handleOpenResident,
  onOpenDefault: handleOpenDefault,
  onVerifyEmail: handleVerifyEmail,
  onVerify,
  onCancelVerify,
  onSendApplicationLink,
  onCancelSendApplicationLink,
}: AboutYourselfReadOnlyViewProps) {
    const safeUserData = userData ?? {};
    const showPersonalInfoActions =
      applicationData?.past_customer === "1" || showPrimaryApplicantActions;

    const isAddressView = view === "address";

    return (
    <div
      className={`${styles.tabContent} ${styles.aboutYourselfPremium}`}
      data-testid={isAddressView ? "application-address-tab-page" : "application-about-tab-page"}
    >
      {!isAddressView && (
      <>
      {/* Personal Information */}
      <div className={styles.section} data-testid="application-about-tab-section-personal-information">
        <div className={styles.personalInfoHeader}>
          <h2 className={styles.personalInfoTitle}>
            <span className={styles.sectionTitleIcon} aria-hidden="true">
              <User className="h-4 w-4" />
            </span>
            Personal Information
          </h2>
          {showPersonalInfoActions && (
            <div className={styles.personalInfoActions}>
              <div className={styles.personalInfoPrimaryButtons}>
                {applicationData?.past_customer === "1" && (
                  <button
                    type="button"
                    className={`${actionBtnStyles.btn} ${actionBtnStyles.btnPrimary}`}
                    data-testid="application-about-tab-previous-apps-button"
                    onClick={() => setPreviousAppsOpen(true)}
                  >
                    Previous Apps
                    {previousAppsCount !== null ? (
                      <span
                        className="absolute -right-2 -top-2 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[11px] font-bold leading-none text-white shadow-sm"
                        data-testid="application-about-tab-previous-apps-count"
                      >
                        {previousAppsCount}
                      </span>
                    ) : null}
                  </button>
                )}
                {showPrimaryApplicantActions && showResidentButton && (
                  <button
                    type="button"
                    className={`${actionBtnStyles.btn} ${actionBtnStyles.btnPrimary}`}
                    data-testid="application-about-tab-resident-button"
                    onClick={() => void handleOpenResident()}
                  >
                    Resident
                  </button>
                )}
                {showPrimaryApplicantActions && showDefaultButton && (
                  <button
                    type="button"
                    className={`${actionBtnStyles.btn} ${actionBtnStyles.btnPrimary}`}
                    data-testid="application-about-tab-default-button"
                    onClick={() => void handleOpenDefault()}
                  >
                    Default
                  </button>
                )}
              </div>
              {showPrimaryApplicantActions && rentalStatusLine && (
                <p className={styles.personalInfoRentalStatus}>{rentalStatusLine}</p>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-12 gap-6 mb-6">
          <div className={`${styles.balancedPersonalInfoGrid} col-span-12 grid grid-cols-12 gap-6`}>
          {/* Row 1 */}
          <div className="col-span-12 md:col-span-4 xl:col-span-3">
            <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid="application-about-tab-view-name-label">
              Name
            </label>
            <div className="text-[13px] font-bold text-[#2d3e50]">
              {safeUserData.first_name} {safeUserData.middle_name} {safeUserData.last_name}
              {safeUserData.suffix ? `, ${safeUserData.suffix}` : ""}
            </div>
          </div>
          <div className="col-span-12 md:col-span-4 xl:col-span-3">
            <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid="application-about-tab-view-phone-number-label">
              Phone Number
            </label>
            <div className="text-[13px] font-bold text-[#2d3e50]">
              {formatPhoneNumber(safeUserData.mobile_no ?? "")}
              {safeUserData.shared_phone_no === "1" && safeUserData.mobile_no && (
                <span className="text-xs font-normal text-gray-500 ml-2">
                  (Shared)
                </span>
              )}
            </div>
          </div>
          <div className="col-span-12 md:col-span-4 xl:col-span-3">
            <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid="application-about-tab-view-communication-preference-label">
              Communication Preference
            </label>
            <div className="text-[13px] font-bold text-[#2d3e50]">
              {applicationData?.communication_preference ||
                (applicationData?.applicant_status !== "NOT_STARTED"
                  ? "Text Msg"
                  : "")}
            </div>
          </div>
          <div className="col-span-12 md:col-span-4 xl:col-span-3">
            <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid="application-about-tab-view-email-address-label">
              Email Address
            </label>
            <div
              className="text-[13px] font-bold text-[#2d3e50]"
              data-testid="application-about-tab-view-email-address-value"
            >
              {formatEmail(safeUserData.email)}
            </div>
          </div>

          {/* Row 2 — Angular: separate columns for DOB and Age (each gets its own display box) */}
          <div className="col-span-12 md:col-span-3 xl:col-span-2">
            <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid="application-about-tab-view-date-of-birth-label">
              Date of Birth
            </label>
            <div
              className="text-[13px] font-bold text-[#2d3e50]"
              data-testid="application-about-tab-view-date-of-birth-value"
            >
              {formatDateMMDDYY(applicationData?.birthdate)}
            </div>
          </div>
          <div className="col-span-12 md:col-span-2 xl:col-span-1">
            <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid="application-about-tab-view-age-label">
              Age
            </label>
            <div
              className="text-[13px] font-bold text-[#2d3e50]"
              data-testid="application-about-tab-view-age-value"
            >
              {calculateAge(applicationData?.birthdate)}
            </div>
          </div>

          <div className="col-span-12 md:col-span-3 xl:col-span-2">
            <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid="application-about-tab-view-ssn-label">
              SSN or ITIN
            </label>
            <div
              className="text-[13px] font-bold text-[#2d3e50]"
              data-testid="application-about-tab-view-ssn-value"
            >
              {applicationData?.security_number
                ? formatSSN(applicationData.security_number)
                : applicationData?.applicant_status !== "NOT_STARTED"
                  ? "XXX-XX-XXXX"
                  : ""}
            </div>
          </div>

        </div>
        </div>
      </div>

      <VerifastIdentitySection
        applicationAllData={applicationAllData}
        applicationData={applicationData}
        applicantId={applicantId}
      />

      <PreviousApplicationsModal
        isOpen={previousAppsOpen}
        onClose={() => setPreviousAppsOpen(false)}
        applicantId={applicantId}
      />
      </>
      )}

      {isAddressView && (
      <>
      {/* Current Address — always visible in view mode (Angular parity) */}
      <div className={styles.section} data-testid="application-about-tab-section-current-address">
        <h2 className="text-xl font-bold text-[#2d3e50] mb-6">
          <span className={styles.sectionTitleIcon} aria-hidden="true">
            <Home className="h-4 w-4" />
          </span>
          Current Address
        </h2>
        {currentAddress?.address_type ? (
          <AddressView
            addressData={currentAddress}
            addressType="current_address"
            canValidateEmail={canValidateEmail}
            canViewAddressOnMap={canViewAddressOnMap}
            onVerifyEmail={handleVerifyEmail}
            verifyingEmail={verifyingEmail}
            testIdPrefix="application-about-tab-current-address"
          />
        ) : null}
      </div>

      {/* Previous Addresses — always visible in view mode (Angular parity) */}
      <div className={styles.section} data-testid="application-about-tab-section-previous-addresses">
        <h2 className={styles.sectionTitle}>
          <span className={styles.sectionTitleIcon} aria-hidden="true">
            <MapPin className="h-4 w-4" />
          </span>
          Previous Address
        </h2>
        {previousAddresses.map((address, index) => (
          <div
            key={address?.ra_id || index}
            className={styles.previousAddressCard}
          >
            <div className={styles.previousAddressCardHeader}>
              Previous Address {index + 1}
            </div>
            <AddressView
              addressData={address}
              addressType="previous_address"
              canValidateEmail={canValidateEmail}
              canViewAddressOnMap={canViewAddressOnMap}
              onVerifyEmail={handleVerifyEmail}
              verifyingEmail={verifyingEmail}
              testIdPrefix={`application-about-tab-previous-address-${index}`}
            />
          </div>
        ))}
      </div>

      {/* Alternate Addresses — only when data exists (Angular parity) */}
      {alternateAddresses.length > 0 && (
        <div className={styles.section} data-testid="application-about-tab-section-alternate-address">
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionTitleIcon} aria-hidden="true">
              <MapPin className="h-4 w-4" />
            </span>
            Alternate Address
          </h2>
          {alternateAddresses.map((address, index) => (
            <div key={index} className={styles.infoRow}>
              <div className={styles.infoColumn}>
                <h6>Address</h6>
                <p>{formatAddressDisplay(address)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )}

      <AboutYourselfConfirmDialogs
        pendingVerifyEmail={pendingVerifyEmail}
        pendingSendLink={pendingSendLink}
        onVerify={onVerify}
        onCancelVerify={onCancelVerify}
        onSendApplicationLink={onSendApplicationLink}
        onCancelSendApplicationLink={onCancelSendApplicationLink}
        testIdPrefix="application-about-tab-confirm"
      />
    </div>
    );
}
